import { useCallback, useMemo, useRef, useState } from "react";

import {
  deleteChatTurn,
  executeManagingPrompt,
  postChatTurn,
  postChatTurnApprove,
} from "../api/ChatApi";
import { buildChatTurnHistory, normaliseTurnMetadata } from "../api/chatTurnUtils";
import { buildQuillDeltaFromMarkdown } from "../utils/buildQuillDelta";
import {
  buildCallRecordingExecuteContext,
  CHAT_PROMPT_CUSTOM_TYPE,
  EMPTY_ASSISTANT_RESPONSE_MESSAGE,
  promptRequiresCallRecording,
} from "../constants/chatConstants";
import type {
  AskPageContext,
  ChatMessage,
  ChatToolCall,
  ChatTurnDeltaEvent,
  ChatTurnFriendlyEvent,
  ChatTurnErrorEvent,
  ChatTurnMetadataEvent,
  ChatTurnPendingConfirmationEvent,
  ChatTurnToolCallResultEvent,
  ChatTurnToolCallStartEvent,
  CrmChatRecordContext,
  ManagingPromptCatalogEntry,
} from "../types/chat.types";
import { toWirePageContext } from "../utils/askPageContext";
import type { ParsedSseEvent } from "../api/chatTurnUtils";
import {
  mapToolStatus,
  toolCallFromPendingConfirmation,
  upsertToolCall,
} from "../utils/chatTurnToolHelpers";

interface UseChatTurnArgs {
  active: boolean;
  workspaceId: string;
  sessionId: string;
  record?: CrmChatRecordContext | null;
  /** GTM / workspace page context for agent loop (story 12.2). */
  pageContext?: AskPageContext | null;
  callRecordingActivityId?: string | null;
  onMessagesChange?: (messages: ChatMessage[]) => void;
  initialMessages?: ChatMessage[];
}

interface SendTurnOptions {
  content: string;
}

interface CatalogPromptOptions {
  prompt: ManagingPromptCatalogEntry;
  content?: string;
  callRecordingActivityId?: string | null;
}

interface UseChatTurnResult {
  messages: ChatMessage[];
  sendTurn: (opts: SendTurnOptions) => Promise<void>;
  runCatalogPrompt: (opts: CatalogPromptOptions) => Promise<void>;
  isStreaming: boolean;
  streamingMessageId: string | null;
  activeTurnId: string | null;
  pendingToolCall: ChatToolCall | null;
  confirmSubmitting: boolean;
  confirmError: string | null;
  approvePendingTool: () => Promise<void>;
  rejectPendingTool: () => Promise<void>;
  error: string | null;
  reset: () => void;
  abortTurn: () => Promise<void>;
  regenerate: (assistantMessageId: string) => Promise<void>;
}

const INACTIVE: UseChatTurnResult = {
  messages: [],
  sendTurn: async () => {},
  runCatalogPrompt: async () => {},
  isStreaming: false,
  streamingMessageId: null,
  activeTurnId: null,
  pendingToolCall: null,
  confirmSubmitting: false,
  confirmError: null,
  approvePendingTool: async () => {},
  rejectPendingTool: async () => {},
  error: null,
  reset: () => {},
  abortTurn: async () => {},
  regenerate: async () => {},
};

function makeId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useChatTurn({
  active,
  workspaceId,
  sessionId,
  record,
  pageContext,
  callRecordingActivityId,
  onMessagesChange,
  initialMessages,
}: UseChatTurnArgs): UseChatTurnResult {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages ?? []);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [pendingToolCall, setPendingToolCall] = useState<ChatToolCall | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeTurnIdRef = useRef<string | null>(null);
  activeTurnIdRef.current = activeTurnId;

  const pushMessages = useCallback(
    (next: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      setMessages((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        onMessagesChange?.(resolved);
        return resolved;
      });
    },
    [onMessagesChange]
  );

  const updateAssistant = useCallback(
    (assistantId: string, updater: (msg: ChatMessage) => ChatMessage) => {
      pushMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? updater(m) : m))
      );
    },
    [pushMessages]
  );

  const handleSseEvent = useCallback(
    (assistantId: string, parsed: ParsedSseEvent) => {
      const { event, data } = parsed;

      if (event === "turn_start" && data && typeof data === "object") {
        const turnId = (data as { turnId?: string }).turnId;
        if (turnId) setActiveTurnId(turnId);
        return;
      }

      if (event === "delta") {
        const delta = data as ChatTurnDeltaEvent;
        if (delta?.content) {
          updateAssistant(assistantId, (m) => {
            const content = m.content + delta.content;
            return {
              ...m,
              content,
              contentDelta: buildQuillDeltaFromMarkdown(content, m.actions),
            };
          });
        }
        return;
      }

      if (event === "friendly") {
        const friendly = data as ChatTurnFriendlyEvent;
        if (friendly?.contentDelta?.ops?.length) {
          updateAssistant(assistantId, (m) => ({
            ...m,
            contentDelta: friendly.contentDelta,
          }));
        }
        return;
      }

      if (event === "tool_call_start") {
        const start = data as ChatTurnToolCallStartEvent;
        const toolCall: ChatToolCall = {
          id: start.id,
          name: start.name,
          arguments: start.arguments ?? {},
          status: "running",
          annotations: start.annotations,
          startedAt: new Date().toISOString(),
        };
        updateAssistant(assistantId, (m) => ({
          ...m,
          toolCalls: [...(m.toolCalls ?? []), toolCall],
        }));
        return;
      }

      if (event === "tool_call_pending_confirmation") {
        const pending = data as ChatTurnPendingConfirmationEvent;
        const toolCall = toolCallFromPendingConfirmation(pending);
        setPendingToolCall(toolCall);
        setConfirmError(null);
        updateAssistant(assistantId, (m) => ({
          ...m,
          toolCalls: upsertToolCall(m.toolCalls ?? [], toolCall),
        }));
        return;
      }

      if (event === "tool_call_result") {
        const result = data as ChatTurnToolCallResultEvent;
        updateAssistant(assistantId, (m) => ({
          ...m,
          toolCalls: (m.toolCalls ?? []).map((tc) =>
            tc.id === result.id
              ? {
                  ...tc,
                  status: mapToolStatus(result.status, result.result),
                  durationMs: result.durationMs,
                  result: result.result,
                  error: result.error
                    ? { code: "tool_error", message: result.error }
                    : undefined,
                  finishedAt: new Date().toISOString(),
                }
              : tc
          ),
        }));
        return;
      }

      if (event === "metadata") {
        const metaEvent = data as ChatTurnMetadataEvent;
        const raw = metaEvent?.metadata as Record<string, unknown> | undefined;
        const suggestedRaw = metaEvent?.suggestedPrompts ?? metaEvent?.suggested_prompts;
        const normalised = normaliseTurnMetadata(raw, suggestedRaw);
        updateAssistant(assistantId, (m) => ({
          ...m,
          metadata: {
            ...m.metadata,
            ...normalised,
            contextSummary: record
              ? { recordId: record.recordId, recordType: record.recordType }
              : m.metadata?.contextSummary,
          },
        }));
        return;
      }

      if (event === "error") {
        const err = data as ChatTurnErrorEvent;
        const hint =
          err.code === "llm_unavailable" && err.retryAfterSeconds
            ? ` Try again in ${err.retryAfterSeconds}s.`
            : "";
        const message = (err.message ?? err.code ?? "Agent loop error") + hint;
        setError(message);
        pushMessages((prev) => [
          ...prev,
          {
            id: makeId("error"),
            sessionId,
            role: "system",
            content: message,
            createdAt: new Date().toISOString(),
            error: message,
          },
        ]);
        return;
      }

      if (event === "done") {
        updateAssistant(assistantId, (m) => {
          const hasContent = m.content.trim().length > 0;
          const hasDelta = (m.contentDelta?.ops?.length ?? 0) > 0;
          const hasTools = (m.toolCalls?.length ?? 0) > 0;
          if (hasContent || hasDelta || hasTools) {
            return m;
          }
          const fallback = EMPTY_ASSISTANT_RESPONSE_MESSAGE;
          return {
            ...m,
            content: fallback,
            contentDelta: buildQuillDeltaFromMarkdown(fallback, m.actions),
          };
        });
        setIsStreaming(false);
        setStreamingMessageId(null);
        setPendingToolCall(null);
        setConfirmError(null);
        setActiveTurnId(null);
      }
    },
    [pushMessages, record, sessionId, updateAssistant]
  );

  const approvePendingTool = useCallback(async () => {
    const turnId = activeTurnIdRef.current;
    const tool = pendingToolCall;
    const assistantId = streamingMessageId;
    if (!tool || !turnId || !assistantId) return;
    setConfirmSubmitting(true);
    setConfirmError(null);
    try {
      await postChatTurnApprove(workspaceId, turnId, { toolCallId: tool.id });
      setPendingToolCall(null);
      updateAssistant(assistantId, (m) => ({
        ...m,
        toolCalls: (m.toolCalls ?? []).map((tc) =>
          tc.id === tool.id ? { ...tc, status: "running" as const } : tc
        ),
      }));
    } catch (err) {
      setConfirmError((err as Error)?.message ?? "Approve failed");
    } finally {
      setConfirmSubmitting(false);
    }
  }, [pendingToolCall, streamingMessageId, updateAssistant, workspaceId]);

  const rejectPendingTool = useCallback(async () => {
    const turnId = activeTurnIdRef.current;
    const tool = pendingToolCall;
    const assistantId = streamingMessageId;
    if (!tool || !turnId || !assistantId) return;
    setConfirmSubmitting(true);
    setConfirmError(null);
    try {
      await postChatTurnApprove(workspaceId, turnId, {
        toolCallId: tool.id,
        decision: "reject",
      });
      setPendingToolCall(null);
      updateAssistant(assistantId, (m) => ({
        ...m,
        toolCalls: (m.toolCalls ?? []).map((tc) =>
          tc.id === tool.id
            ? { ...tc, status: "rejected" as const, finishedAt: new Date().toISOString() }
            : tc
        ),
      }));
    } catch (err) {
      setConfirmError((err as Error)?.message ?? "Reject failed");
    } finally {
      setConfirmSubmitting(false);
    }
  }, [pendingToolCall, streamingMessageId, updateAssistant, workspaceId]);

  const sendTurn = useCallback(
    async ({ content }: SendTurnOptions) => {
      if (!active) return;
      const trimmed = content.trim();
      if (!trimmed) {
        setError("Please type a message before sending.");
        return;
      }

      const now = new Date().toISOString();
      const userMessage: ChatMessage = {
        id: makeId("user"),
        sessionId,
        role: "user",
        content: trimmed,
        createdAt: now,
      };

      const assistantId = makeId("assistant");
      const assistantMessage: ChatMessage = {
        id: assistantId,
        sessionId,
        role: "assistant",
        content: "",
        createdAt: now,
        toolCalls: [],
      };

      const history = buildChatTurnHistory(messages);
      pushMessages((prev) => [...prev, userMessage, assistantMessage]);
      setError(null);
      setIsStreaming(true);
      setStreamingMessageId(assistantId);
      setActiveTurnId(null);
      setPendingToolCall(null);
      setConfirmError(null);

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        await postChatTurn({
          workspaceId,
          body: {
            sessionId,
            content: trimmed,
            recordContext: record
              ? {
                  recordId: record.recordId,
                  recordType: record.recordType,
                  displayName: record.displayName,
                }
              : undefined,
            pageContext: pageContext ? toWirePageContext(pageContext) : undefined,
            history,
          },
          signal: controller.signal,
          onEvent: (ev) => handleSseEvent(assistantId, ev),
        });
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        const message = (err as Error)?.message ?? "Chat turn failed.";
        setError(message);
        pushMessages((prev) => [
          ...prev,
          {
            id: makeId("error"),
            sessionId,
            role: "system",
            content: message,
            createdAt: new Date().toISOString(),
            error: message,
          },
        ]);
      } finally {
        setIsStreaming(false);
        setStreamingMessageId(null);
        abortControllerRef.current = null;
      }
    },
    [active, handleSseEvent, messages, pageContext, pushMessages, record, sessionId, workspaceId]
  );

  const runCatalogPrompt = useCallback(
    async ({
      prompt,
      content,
      callRecordingActivityId: promptCallRecordingActivityId,
    }: CatalogPromptOptions) => {
      if (!active) return;
      if (prompt.promptType === CHAT_PROMPT_CUSTOM_TYPE) return;

      const trimmedContent = content?.trim() ?? "";
      if (
        promptRequiresCallRecording(prompt) &&
        !(promptCallRecordingActivityId ?? callRecordingActivityId)
      ) {
        setError("Select a call or meeting with a transcript before running this prompt.");
        return;
      }

      const activityId = promptCallRecordingActivityId ?? callRecordingActivityId;
      const context =
        activityId != null ? buildCallRecordingExecuteContext(activityId) : undefined;

      const body = {
        recordId: record?.recordId,
        language: "en",
        ...(context ? { context } : {}),
        ...(trimmedContent ? { customPrompt: trimmedContent } : {}),
      };

      const userMessage: ChatMessage = {
        id: makeId("user"),
        sessionId,
        role: "user",
        content: trimmedContent || prompt.name,
        createdAt: new Date().toISOString(),
        promptType: prompt.promptType,
        promptName: prompt.name,
      };

      pushMessages((prev) => [...prev, userMessage]);
      setError(null);
      setIsStreaming(true);

      try {
        const result = await executeManagingPrompt(workspaceId, prompt.promptType, body);
        const assistant: ChatMessage = {
          id: makeId("assistant"),
          sessionId,
          role: "assistant",
          content: result.value,
          contentDelta: result.contentDelta,
          actions: result.actions?.length ? result.actions : undefined,
          createdAt: new Date().toISOString(),
          promptType: result.promptType,
          promptName: prompt.name,
          metadata: {
            ...result.metadata,
            contextSummary: record
              ? { recordId: record.recordId, recordType: record.recordType }
              : undefined,
          },
        };
        pushMessages((prev) => [...prev, assistant]);
      } catch (err) {
        const errData = (err as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data;
        const message =
          errData?.message ??
          errData?.error ??
          (err as Error)?.message ??
          "Something went wrong while generating a response.";
        setError(message);
        pushMessages((prev) => [
          ...prev,
          {
            id: makeId("error"),
            sessionId,
            role: "system",
            content: message,
            createdAt: new Date().toISOString(),
            error: message,
          },
        ]);
      } finally {
        setIsStreaming(false);
      }
    },
    [active, callRecordingActivityId, pushMessages, record, sessionId, workspaceId]
  );

  const abortTurn = useCallback(async () => {
    abortControllerRef.current?.abort();
    if (activeTurnId) {
      try {
        await deleteChatTurn(workspaceId, activeTurnId);
      } catch {
        // best-effort
      }
    }
    if (streamingMessageId) {
      updateAssistant(streamingMessageId, (m) => ({
        ...m,
        toolCalls: (m.toolCalls ?? []).map((tc) =>
          tc.status === "running" ? { ...tc, status: "aborted" as const } : tc
        ),
      }));
    }
    setIsStreaming(false);
    setStreamingMessageId(null);
    setActiveTurnId(null);
    setPendingToolCall(null);
    setConfirmError(null);
  }, [activeTurnId, streamingMessageId, updateAssistant, workspaceId]);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    pushMessages([]);
    setError(null);
    setIsStreaming(false);
    setStreamingMessageId(null);
    setActiveTurnId(null);
    setPendingToolCall(null);
    setConfirmError(null);
  }, [pushMessages]);

  const regenerate = useCallback(
    async (assistantMessageId: string) => {
      const idx = messages.findIndex((m) => m.id === assistantMessageId);
      if (idx <= 0) return;
      const priorUser = [...messages.slice(0, idx)].reverse().find((m) => m.role === "user");
      if (!priorUser) return;
      pushMessages((prev) => prev.slice(0, idx));
      await sendTurn({ content: priorUser.content });
    },
    [messages, pushMessages, sendTurn]
  );

  return useMemo(
    () =>
      active
        ? {
            messages,
            sendTurn,
            runCatalogPrompt,
            isStreaming,
            streamingMessageId,
            activeTurnId,
            pendingToolCall,
            confirmSubmitting,
            confirmError,
            approvePendingTool,
            rejectPendingTool,
            error,
            reset,
            abortTurn,
            regenerate,
          }
        : INACTIVE,
    [
      active,
      messages,
      sendTurn,
      runCatalogPrompt,
      isStreaming,
      streamingMessageId,
      activeTurnId,
      pendingToolCall,
      confirmSubmitting,
      confirmError,
      approvePendingTool,
      rejectPendingTool,
      error,
      reset,
      abortTurn,
      regenerate,
    ]
  );
}
